import { IonPage } from '@ionic/react';
import { ReactNode, useState } from 'react';
import {
  LuAlertTriangle,
  LuArrowRight,
  LuCheck,
  LuClock,
  LuKey,
  LuLock,
  LuLogIn,
  LuMail,
  LuPhone,
  LuShield,
  LuSmartphone,
} from 'react-icons/lu';

import PepperLogo from '@/assets/imgs/pepper-logo.svg';
import AppleStore from '@/assets/imgs/seguridad-social/apple.png';
import GooglePlay from '@/assets/imgs/seguridad-social/google.png';
import Button from '@/components/common/Button/Button';

import styles from './AltaClavePin.module.scss';
import {
  CVDatos,
  CVIdent,
  CVPin,
  CVRegistro,
  CVVias,
  CVVidDni,
  CVVidOk,
  CVVidSelfie,
  PhoneFrame,
} from './ClaveScreens';

const CLAVE_URL = 'https://clave.gob.es/clave_Home/registro.html';
const TEL = 'tel:+34912161212';
const IOS_URL = 'https://apps.apple.com/es/app/cl-ve/id842624380';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=es.aeat.pin24h&hl=es';

const STEPS = [
  {
    n: 1,
    Icon: LuLogIn,
    Screen: CVRegistro,
    url: 'clave.gob.es',
    title: 'Entra en «Registrarse en Cl@ve»',
    desc: 'Abre el registro en el portal de Cl@ve. Es un alta única para todos los organismos.',
    tip: 'Ten a mano DNI o NIE, móvil y correo.',
    tipHref: CLAVE_URL,
  },
  {
    n: 2,
    Icon: LuShield,
    Screen: CVIdent,
    url: 'sede.agenciatributaria.gob.es',
    title: 'Identifica tu documento',
    desc: 'Escribe tu DNI o NIE sin guiones. Te pedirá la fecha de validez o el número de soporte.',
    tip: 'El número de soporte del NIE empieza por «E».',
  },
  {
    n: 3,
    Icon: LuKey,
    Screen: CVVias,
    url: 'sede.agenciatributaria.gob.es',
    title: 'Elige cómo verificar tu identidad',
    desc: 'Al momento con la app Cl@ve o con certificado o DNIe. Si no, pide la carta de invitación.',
    tip: 'La videoidentificación es la vía más rápida.',
  },
  {
    n: 4,
    Icon: LuMail,
    Screen: CVDatos,
    url: 'sede.agenciatributaria.gob.es',
    title: 'Añade tu móvil y tu correo',
    desc: 'Indica el móvil donde recibir el PIN y tu correo. Acepta las condiciones y confirma.',
    tip: 'Usa un móvil que sea solo tuyo.',
  },
  {
    n: 5,
    Icon: LuCheck,
    Screen: CVPin,
    url: 'clave.gob.es',
    title: 'Pide tu PIN cuando lo necesites',
    desc: 'Ya estás de alta. Pide el PIN desde la app cada vez que lo necesites.',
    tip: 'Te identificas al momento, sin depender del SMS.',
  },
];

const BRANCHES = [
  {
    id: 'video',
    tone: 'success',
    tag: 'AL MOMENTO',
    Icon: LuSmartphone,
    title: 'Videoidentificación con la app',
    lead: 'La vía más rápida: te identificas grabándote con el móvil.',
    body: 'Descarga la app Cl@ve, elige registrarte por videoidentificación y sigue las instrucciones: foto del DNI por las dos caras y un vídeo corto. El alta es inmediata con nivel básico y, si un empleado público la revisa, pasa a nivel avanzado.',
  },
  {
    id: 'cert',
    tone: 'neutral',
    tag: 'NIVEL AVANZADO',
    Icon: LuShield,
    title: 'Con certificado digital o DNIe',
    lead: 'Si ya tienes certificado o DNIe, el alta también es inmediata.',
    body: 'Entra en «Registrarse en Cl@ve», identifícate con tu certificado electrónico o tu DNIe, revisa tus datos de contacto y confirma. Obtienes directamente el nivel avanzado, que abre todos los trámites.',
  },
  {
    id: 'carta',
    tone: 'warning',
    tag: 'CARTA POSTAL',
    Icon: LuMail,
    title: 'Con carta de invitación',
    lead: 'Sin certificado y sin vídeo, pero hay que esperar unos días.',
    body: 'Pide la carta desde el registro en Cl@ve o desde la app. Llegará a tu domicilio fiscal con un Código Seguro de Verificación (CSV) de 16 caracteres. Vuelve al registro, marca «Ya dispongo de una carta de invitación» e introduce el CSV antes de que pasen 60 días.',
  },
];

const VIDEO_CARDS = [
  {
    n: 1,
    Screen: CVVidDni,
    title: 'Fotografía tu DNI',
    text: 'Por el anverso y el reverso, con buena luz y sin reflejos.',
  },
  {
    n: 2,
    Screen: CVVidSelfie,
    title: 'Grábate un vídeo corto',
    text: 'Unos segundos mirando a la cámara para comprobar que eres tú.',
  },
  {
    n: 3,
    Screen: CVVidOk,
    title: 'Alta confirmada',
    text: 'La app te confirma el alta al momento y ya puedes pedir tu PIN.',
  },
];

const WHY = [
  {
    Icon: LuKey,
    title: 'Una identidad para todo',
    text: 'Una sola alta para todos los trámites con la Administración.',
  },
  {
    Icon: LuClock,
    title: 'En unos minutos',
    text: 'Con la app o con certificado el alta es inmediata.',
  },
  {
    Icon: LuShield,
    title: 'Sin papeleo con Pepper',
    text: 'Accede a tus datos oficiales y sigue con tu financiación sin papeles.',
  },
];

const FAQ = [
  {
    q: '¿Para qué necesito Cl@ve si voy a financiar con Pepper?',
    a: 'Para financiar sin papeleo comprobamos tu identidad con fuentes oficiales. Con Cl@ve te identificas al momento y das el visto bueno tú mismo, sin depender de que te llegue un código por SMS.',
  },
  {
    q: '¿Qué es el PIN y qué es Cl@ve Permanente?',
    a: 'Son dos formas de usar la misma cuenta de Cl@ve. El PIN es un código de seis dígitos de un solo uso que recibes por SMS cada vez que haces un trámite; Cl@ve Permanente es una contraseña fija. El alta es la misma para las dos.',
  },
  {
    q: '¿Cuánto tarda en llegar la carta de invitación?',
    a: 'Depende del correo postal: normalmente entre 5 y 10 días laborables. El CSV que incluye caduca a los 60 días. Si se te pasa, puedes pedir otra carta o darte de alta por videoidentificación.',
  },
  {
    q: '¿Qué diferencia hay entre nivel básico y nivel avanzado?',
    a: 'El alta por carta de invitación o por videoidentificación automática da nivel básico, suficiente para la mayoría de trámites. Algunos servicios piden nivel avanzado: lo consigues con certificado digital, DNIe, revisión de tu vídeo por un empleado público o registro presencial.',
  },
  {
    q: '¿Puedo darme de alta en persona?',
    a: 'Sí. Acude con tu DNI o NIE a una oficina de registro Cl@ve y sales con el nivel avanzado. Pide cita previa antes de ir.',
  },
  {
    q: 'Soy del comercio: ¿puedo hacer el alta por mi cliente?',
    a: 'El alta es personal e intransferible: cada persona se registra con su documento y su móvil. Lo que sí puedes es acompañarle en el proceso desde el móvil del cliente y, cuando termine, volver a pasar la operación.',
  },
];

// En esta página casi todo el texto menciona Cl@ve: solo se enlaza «app Cl@ve»
// a la sección de descarga.
function linkClave(text: string): ReactNode {
  if (!text.includes('app Cl@ve')) return text;
  let offset = 0;
  return text.split(/(app Cl@ve)/g).map((part) => {
    const key = `clave-${offset}`;
    offset += part.length;
    if (part !== 'app Cl@ve') return part;
    return (
      <a key={key} href="#clave" className={styles.claveLink}>
        {part}
      </a>
    );
  });
}

const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ── Sub-components ──────────────────────────────────────────────────

function BrowserFrame({
  url,
  children,
  step = false,
}: {
  url: string;
  children: ReactNode;
  step?: boolean;
}) {
  return (
    <div className={`${styles.browserChrome} ${step ? styles.browserChromeStep : ''}`}>
      <div className={styles.browserBar}>
        <span className={styles.browserDots}>
          <span className={styles.browserDotRed} />
          <span className={styles.browserDotYellow} />
          <span className={styles.browserDotGreen} />
        </span>
        <span className={styles.browserUrl}>
          <LuLock size={11} className={styles.browserLock} />
          {url}
        </span>
      </div>
      {children}
    </div>
  );
}

function Nav({ onCta }: { onCta: () => void }) {
  return (
    <header className={styles.nav}>
      <div className={`${styles.shell} ${styles.navInner}`}>
        <img src={PepperLogo} alt="Pepper" className={styles.navLogo} />
        <div className={styles.navActions}>
          <a href="#pasos" className={styles.navLink}>
            Cómo darte de alta
          </a>
          <a href="#video" className={styles.navLink}>
            Videoidentificación
          </a>
          <a href="#dudas" className={styles.navLink}>
            Dudas
          </a>
          <Button
            id="nav-cta"
            onClick={onCta}
            shape="round"
            className={styles.navCta}
          >
            Registrarme en Cl@ve
          </Button>
        </div>
      </div>
    </header>
  );
}

function Hero({ onCta }: { onCta: () => void }) {
  return (
    <section className={styles.hero}>
      <div className={`${styles.shell} ${styles.heroGrid}`}>
        <div>
          <h1 className={styles.heroTitle}>
            Date de alta en
            {' '}
            <span className={styles.red}>Cl@ve</span>
          </h1>
          <p className={styles.heroSub}>
            Cl@ve es la identidad digital con la que te identificas ante la
            Administración. Te identificas tú al momento, sin esperar códigos
            por SMS. Se hace una vez y, con la app, en unos minutos.
          </p>
          <div className={styles.heroBtns}>
            <Button id="hero-clave" onClick={onCta} shape="round">
              Registrarme en Cl@ve
            </Button>
            <Button
              id="hero-how"
              color="dark"
              fill="outline"
              onClick={() => scrollTo('pasos')}
              shape="round"
            >
              Ver cómo funciona
            </Button>
          </div>
        </div>
        <div className={styles.heroArt}>
          <BrowserFrame url="clave.gob.es">
            <CVVias />
          </BrowserFrame>
        </div>
      </div>
    </section>
  );
}

function Stepper({ onCta }: { onCta: () => void }) {
  const [step, setStep] = useState(0);
  const s = STEPS[step];
  return (
    <section className={styles.stepperSection}>
      <div className={styles.shell}>
        <div className={styles.stepperHeader}>
          <div className={styles.eyebrow}>Paso a paso</div>
          <h2 className={styles.sectionH2}>Cómo darte de alta en Cl@ve</h2>
        </div>
        <div id="pasos" className={styles.stepperProgress}>
          {STEPS.map((st, k) => (
            <button
              key={st.n}
              type="button"
              onClick={() => setStep(k)}
              className={styles.stepperTrack}
            >
              <div
                className={`${styles.stepperBar} ${k <= step ? styles.stepperBarActive : ''}`}
              />
              <div
                className={`${styles.stepperLabelRow} ${k === step ? styles.stepperLabelActive : ''}`}
              >
                <st.Icon
                  size={15}
                  color={
                    k <= step
                      ? 'var(--ion-color-pepper-red-6)'
                      : 'var(--ion-color-pepper-gray-6)'
                  }
                />
                <span>{`Paso ${st.n}`}</span>
              </div>
            </button>
          ))}
        </div>
        <div className={styles.stepperGrid}>
          <div>
            <div className={styles.stepperMeta}>
              <div className={styles.stepBadge}>{s.n}</div>
              <span className={styles.stepperCounter}>
                {`Paso ${s.n} de ${STEPS.length}`}
              </span>
            </div>
            <h3 className={styles.stepperTitle}>{s.title}</h3>
            <p className={styles.stepperDesc}>{linkClave(s.desc)}</p>
            {s.tip && (
              <div className={styles.tipBox}>
                <LuAlertTriangle size={18} className={styles.tipIcon} />
                <p className={styles.tipText}>
                  {s.tipHref ? (
                    <a
                      href={s.tipHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.tipLink}
                    >
                      {s.tip}
                      <LuArrowRight size={15} />
                    </a>
                  ) : (
                    linkClave(s.tip)
                  )}
                </p>
              </div>
            )}
            <div className={styles.stepperNav}>
              <Button
                id="step-prev"
                color="dark"
                fill="outline"
                disabled={step === 0}
                onClick={() => setStep(Math.max(0, step - 1))}
                shape="round"
              >
                ← Anterior
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  id="step-next"
                  onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
                  shape="round"
                >
                  Siguiente →
                </Button>
              ) : (
                <Button
                  id="step-done"
                  color="success"
                  onClick={onCta}
                  shape="round"
                >
                  ¡Listo! Ir a Cl@ve
                </Button>
              )}
            </div>
          </div>
          <div key={s.n} className={styles.stepperArt}>
            <BrowserFrame url={s.url} step>
              <s.Screen />
            </BrowserFrame>
          </div>
        </div>
      </div>
    </section>
  );
}

function BranchSection() {
  const [open, setOpen] = useState<string>('video');
  const active = BRANCHES.find((b) => b.id === open)!;
  return (
    <section id="vias" className={styles.branchSection}>
      <div className={styles.shell}>
        <div className={styles.branchHeader}>
          <div className={styles.eyebrow}>Según tu caso</div>
          <h2 className={styles.sectionH2}>
            ¿Qué vía de registro te conviene?
          </h2>
          <p className={styles.sectionSub}>
            Las tres valen para pedir tu PIN. Cambian el tiempo de espera y el
            nivel de registro.
          </p>
        </div>
        <div className={styles.branchGrid}>
          {BRANCHES.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setOpen(b.id)}
              className={`${styles.branchCard} ${styles[`branchCard_${b.tone}`]} ${b.id === open ? styles.branchCardActive : ''}`}
            >
              <div className={styles.branchCardTop}>
                <div
                  className={`${styles.branchIconWrap} ${styles[`branchIconWrap_${b.tone}`]}`}
                >
                  <b.Icon size={22} />
                </div>
                <span
                  className={`${styles.branchTag} ${styles[`branchTag_${b.tone}`]}`}
                >
                  {b.tag}
                </span>
              </div>
              <h3 className={styles.branchTitle}>{b.title}</h3>
              <p className={styles.branchLead}>{b.lead}</p>
            </button>
          ))}
        </div>
        <div
          className={`${styles.branchPanel} ${styles[`branchPanel_${active.tone}`]}`}
        >
          <div className={styles.branchPanelRow}>
            <div
              className={`${styles.branchIconWrap} ${styles.branchIconWrapRound} ${styles[`branchIconWrap_${active.tone}`]}`}
            >
              <active.Icon size={22} />
            </div>
            <div>
              <strong className={styles.branchPanelTitle}>
                {active.title}
              </strong>
              <p className={styles.branchBody}>{linkClave(active.body)}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VideoIdSection({ onCta }: { onCta: () => void }) {
  return (
    <section id="video" className={styles.videoIdSection}>
      <div className={styles.shell}>
        <div className={styles.videoIdHeader}>
          <div className={styles.videoIdBadge}>
            <LuCheck size={15} />
            VÍA RECOMENDADA
          </div>
          <h2 className={styles.sectionH2}>
            Videoidentificación: el alta en unos minutos
          </h2>
          <p className={styles.sectionSub}>
            Es 100% online e inmediata. Solo necesitas la app Cl@ve, tu DNI o
            NIE físico y un sitio con buena luz: no esperas ninguna carta ni
            pides cita.
          </p>
        </div>
        <div className={styles.videoIdGrid}>
          {VIDEO_CARDS.map((c) => (
            <div key={c.n} className={styles.videoIdCard}>
              <PhoneFrame maxWidth={240}>
                <c.Screen />
              </PhoneFrame>
              <div className={styles.videoIdCardHead}>
                <div className={styles.videoIdCardBadge}>{c.n}</div>
                <h3 className={styles.videoIdCardTitle}>{c.title}</h3>
              </div>
              <p className={styles.videoIdCardText}>{c.text}</p>
            </div>
          ))}
        </div>
        <div className={styles.videoIdActions}>
          <Button id="videoid-cta" onClick={onCta} shape="round">
            Empezar mi alta con la app
          </Button>
          <a href="#clave" className={styles.ctaStepsLink}>
            Descargar la app Cl@ve
          </a>
        </div>
        <p className={styles.videoIdNote}>
          El alta por vídeo te da nivel básico al momento; si un empleado
          público revisa tu vídeo, pasa a nivel avanzado.
        </p>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className={styles.why}>
      <div className={styles.shell}>
        <div className={styles.whyHeader}>
          <h2 className={styles.sectionH2}>
            ¿Por qué te interesa tener Cl@ve?
          </h2>
          <p className={styles.sectionSub}>
            Es gratis, se pide una sola vez y te ahorra desplazamientos.
          </p>
        </div>
        <div className={styles.whyGrid}>
          {WHY.map((w) => (
            <div key={w.title} className={styles.whyCard}>
              <div className={styles.whyIcon}>
                <w.Icon size={21} strokeWidth={2.75} />
              </div>
              <div>
                <h3 className={styles.whyTitle}>{w.title}</h3>
                <p className={styles.whyText}>{w.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState<number>(0);
  return (
    <section id="dudas" className={styles.faqSection}>
      <div className={styles.shell}>
        <div className={styles.faqGrid}>
          <div>
            <h2 className={`${styles.sectionH2} ${styles.sectionH2Left}`}>
              Dudas frecuentes
            </h2>
            <p className={`${styles.sectionSub} ${styles.sectionSubLeft}`}>
              Lo que más preguntan clientes y comercios sobre el alta en Cl@ve.
            </p>
          </div>
          <div>
            {FAQ.map((f, k) => (
              <div key={f.q} className={styles.faqItem}>
                <button
                  type="button"
                  onClick={() => setOpen(open === k ? -1 : k)}
                  className={styles.faqQuestion}
                >
                  <span>{f.q}</span>
                  <span
                    className={`${styles.faqChevron} ${open === k ? styles.faqChevronOpen : ''}`}
                  >
                    ›
                  </span>
                </button>
                <div
                  className={`${styles.faqAnswer} ${open === k ? styles.faqAnswerOpen : ''}`}
                >
                  <p>{linkClave(f.a)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onCta }: { onCta: () => void }) {
  return (
    <section className={styles.ctaSection}>
      <div className={styles.shell}>
        <div className={styles.ctaBox}>
          <div className={styles.ctaAccent} />
          <div className={styles.ctaSplit}>
            <div>
              <h2 className={styles.ctaTitle}>
                Un paso más y tu financiación está lista
              </h2>
              <p className={styles.ctaSub}>
                Date de alta en Cl@ve para identificarte y terminar tu
                financiación con Pepper. Se hace una vez y te vale para siempre.
              </p>
              <div className={styles.ctaActions}>
                <Button
                  id="cta-clave"
                  onClick={onCta}
                  shape="round"
                  className={styles.ctaBtn}
                >
                  <span className={styles.ctaBtnInner}>
                    Registrarme en Cl@ve
                    <LuArrowRight size={20} />
                  </span>
                </Button>
                <a href="#pasos" className={styles.ctaStepsLink}>
                  Ver los pasos otra vez
                </a>
              </div>
            </div>
            <div className={styles.ctaDivider} />
            <div>
              <h3 className={styles.ctaPhoneTitle}>¿Alguna pregunta?</h3>
              <p className={styles.ctaPhoneSub}>
                Llámanos al
                {' '}
                <a href={TEL} className={styles.ctaPhoneNum}>
                  912 16 12 12
                </a>
                {' '}
                o contacta con tu comercial. Estamos aquí para ayudarte a
                terminar.
              </p>
              <a href={TEL} className={styles.ctaPhoneLink}>
                <LuPhone size={16} />
                {' '}
                Llamar al 912 16 12 12
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClaveAppSection() {
  return (
    <section id="clave" className={styles.claveSection}>
      <div className={`${styles.shell} ${styles.claveGrid}`}>
        <div>
          <div className={styles.claveEyebrowRow}>
            <div className={styles.claveEyebrowIcon}>
              <LuKey size={22} />
            </div>
            <div className={styles.eyebrow}>App Cl@ve</div>
          </div>
          <h2 className={`${styles.sectionH2} ${styles.sectionH2Left}`}>
            Descarga la app Cl@ve
          </h2>
          <p className={`${styles.sectionSub} ${styles.sectionSubLeft}`}>
            Desde la app puedes darte de alta por videoidentificación, pedir tu
            PIN y confirmar tus accesos. Es gratuita y muchos ya la tienen desde
            los ERTE de 2020: busca el icono naranja antes de descargarla.
          </p>
        </div>
        <div className={styles.claveBtns}>
          <a
            href={IOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.storeBtn}
          >
            <img src={AppleStore} alt="Consíguelo en el App Store" />
          </a>
          <a
            href={ANDROID_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.storeBtn}
          >
            <img src={GooglePlay} alt="Disponible en Google Play" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <img src={PepperLogo} alt="Pepper" className={styles.footerLogo} />
        <p className={styles.footerCopy}>
          Copyright 2026 Pepper Finance Corporation, S.L.U. Todos los derechos
          reservados.
        </p>
      </div>
    </footer>
  );
}

// ── Page ────────────────────────────────────────────────────────────

export const AltaClavePin = () => {
  const openClave = () => window.open(CLAVE_URL, '_blank', 'noopener,noreferrer');

  return (
    <IonPage>
      <div className={styles.page}>
        <Nav onCta={openClave} />
        <Hero onCta={openClave} />
        <Stepper onCta={openClave} />
        <BranchSection />
        <VideoIdSection onCta={openClave} />
        <WhySection />
        <FaqSection />
        <FinalCta onCta={openClave} />
        <ClaveAppSection />
        <Footer />
      </div>
    </IonPage>
  );
};
